/**
 * Slack-like light rich-text composer (TipTap).
 * Formatting toolbar + Aa toggle + typing surface; markdown I/O for persistence.
 * Keyboard shortcuts match Slack desktop formatting (see slackKeyboardShortcuts).
 * Optional bottom chrome: + menu, emoji palette, @ mention typeahead.
 * Bottom panels portal to document.body so overflow parents cannot clip them.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import type { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { Markdown } from 'tiptap-markdown';
import { isSafeHttpUrl, SlackKeyboardShortcuts } from './slackKeyboardShortcuts';
import { COMPOSER_EMOJI_CATEGORIES, filterComposerEmojis } from './composerEmojis';
import { applyComposerLinkUrl } from './composerLink';
import { runFormatCommand, type FormatCommand } from './richTextFormat';
import { RichTextBottomChrome } from './RichTextBottomChrome';
import { RichTextToolbar } from './RichTextToolbar';
import './richTextComposer.css';

export interface ComposerMentionCandidate {
  id: string;
  label: string;
  /** Destination account label shown beside the person name. */
  aliasLabel?: string;
  /** Structured person mention metadata; legacy Slack candidates only need id/label. */
  personId?: string;
  aliasId?: string;
  platform?: 'slack' | 'jira' | 'github';
  disabled?: boolean;
  disabledReason?: string;
}

export interface RichTextComposerProps {
  /** Markdown value (storage format). */
  value: string;
  onChange: (markdown: string) => void;
  placeholder?: string;
  minHeight?: number;
  /** Show + / emoji / @ bottom chrome (Slack reply composer). */
  showBottomExtras?: boolean;
  /** Show the bottom Aa toolbar visibility toggle. */
  showToolbarToggle?: boolean;
  /** Candidates for the @ mention typeahead when showBottomExtras is on. */
  mentionCandidates?: ComposerMentionCandidate[];
  /** Called when a person has no confirmed alias for this destination. */
  onMentionCandidateUnavailable?: (candidate: ComposerMentionCandidate) => void;
  toolbarDefaultVisible?: boolean;
  /**
   * When true, Enter submits (Slack reply / Daily add-edit). Style notes keep
   * multiline: Enter inserts a paragraph; Shift+Enter hard-breaks (TipTap default).
   */
  submitOnEnter?: boolean;
  onSubmit?: () => void;
  /** Escape handler (e.g. cancel Daily inline edit). */
  onCancel?: () => void;
  /** Optional hook for tests / parent chrome (editor instance ready). */
  onEditorReady?: (editor: Editor) => void;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
  onBlur?: () => void;
  ariaLabel?: string;
  enableImages?: boolean;
  onSetReminder?: (due: string, time?: string) => void;
  onRemoveReminder?: () => void;
  currentDue?: string;
  currentTime?: string;
  enableReminders?: boolean;
  toolbarTrailing?: React.ReactNode;
  autoFocus?: boolean;
}

type BottomPanel = 'plus' | 'emoji' | 'mention' | null;

function isSlackMentionHref(href: string): boolean {
  return /^slack-mention:[UW][A-Z0-9]+$/i.test(href);
}

function isPersonMentionHref(href: string): boolean {
  return /^person-mention:(slack|jira|github):[^:]+:[^:]+$/i.test(href);
}

export function handleEscapeKey(options: {
  event: { key: string; preventDefault: () => void; stopPropagation?: () => void };
  bottomPanel: BottomPanel;
  clearPanels: () => void;
  onCancel?: () => void;
}): boolean {
  if (options.event.key !== 'Escape') return false;
  if (options.bottomPanel) {
    options.event.preventDefault();
    options.event.stopPropagation?.();
    options.clearPanels();
    return true;
  }
  if (!options.onCancel) return false;
  options.event.preventDefault();
  options.event.stopPropagation?.();
  options.onCancel();
  return true;
}

export function handleComposerKeyDown(options: {
  event: { key: string; shiftKey?: boolean; preventDefault: () => void };
  bottomPanel: BottomPanel;
  submitOnEnter: boolean;
  clearPanels: () => void;
  onCancel?: () => void;
  onSubmit?: () => void;
}): boolean {
  if (handleEscapeKey(options)) return true;
  if (!options.submitOnEnter || options.event.key !== 'Enter' || options.event.shiftKey) {
    return false;
  }
  options.event.preventDefault();
  options.onSubmit?.();
  return true;
}

export const RichTextComposer: React.FC<RichTextComposerProps> = ({
  value,
  onChange,
  placeholder = 'Escribe…',
  minHeight = 96,
  showBottomExtras = false,
  showToolbarToggle = true,
  mentionCandidates = [],
  onMentionCandidateUnavailable,
  toolbarDefaultVisible = true,
  submitOnEnter = false,
  onSubmit,
  onCancel,
  onEditorReady,
  disabled = false,
  className = '',
  'data-testid': testId,
  onBlur,
  ariaLabel = 'Editor de texto enriquecido',
  enableImages = false,
  onSetReminder,
  onRemoveReminder,
  currentDue,
  currentTime,
  enableReminders,
  toolbarTrailing,
  autoFocus = false,
}) => {
  const [toolbarVisible, setToolbarVisible] = useState(toolbarDefaultVisible);
  const [bottomPanel, setBottomPanel] = useState<BottomPanel>(null);
  const [mentionQuery, setMentionQuery] = useState('');
  const [emojiQuery, setEmojiQuery] = useState('');
  const [emojiCategoryId, setEmojiCategoryId] = useState(
    COMPOSER_EMOJI_CATEGORIES[0]?.id ?? 'smileys',
  );
  const lastEmittedRef = useRef(value || '');
  const rootRef = useRef<HTMLDivElement | null>(null);
  const plusTriggerRef = useRef<HTMLButtonElement | null>(null);
  const emojiTriggerRef = useRef<HTMLButtonElement | null>(null);
  const mentionTriggerRef = useRef<HTMLButtonElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const bottomPanelRef = useRef<BottomPanel>(null);
  const onSubmitRef = useRef(onSubmit);
  const onCancelRef = useRef(onCancel);
  onSubmitRef.current = onSubmit;
  onCancelRef.current = onCancel;
  bottomPanelRef.current = bottomPanel;

  const closePanels = useCallback(() => {
    setBottomPanel(null);
    setMentionQuery('');
    setEmojiQuery('');
  }, []);

  const editor = useEditor({
    immediatelyRender: false,
    editable: !disabled,
    extensions: [
      StarterKit.configure({
        heading: false,
        horizontalRule: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        protocols: ['http', 'https', 'slack-mention', 'person-mention'],
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
        isAllowedUri: (url, ctx) => {
          if (url.startsWith('slack-mention:')) return isSlackMentionHref(url);
          if (url.startsWith('person-mention:')) return isPersonMentionHref(url);
          return Boolean(ctx.defaultValidate(url) && isSafeHttpUrl(url));
        },
        shouldAutoLink: (url) => isSafeHttpUrl(url),
      }),
      ...(enableImages
        ? [Image.configure({ allowBase64: true, HTMLAttributes: { class: 'z-rtc-image' } })]
        : []),
      Placeholder.configure({ placeholder }),
      Markdown.configure({
        html: false,
        transformPastedText: true,
        transformCopiedText: true,
      }),
      SlackKeyboardShortcuts,
    ],
    content: value || '',
    onUpdate: ({ editor: ed }) => {
      const markdown = ed.storage.markdown.getMarkdown() as string;
      lastEmittedRef.current = markdown;
      onChange(markdown);
    },
    editorProps: {
      attributes: {
        'aria-label': ariaLabel,
        role: 'textbox',
        'aria-multiline': 'true',
        class: 'z-rtc-prose',
      },
      handleDOMEvents: {
        blur: () => {
          onBlur?.();
          return false;
        },
      },
      handleKeyDown: (_view, event) =>
        handleComposerKeyDown({
          event,
          bottomPanel: bottomPanelRef.current,
          submitOnEnter,
          clearPanels: () => {
            setBottomPanel(null);
            setMentionQuery('');
            setEmojiQuery('');
          },
          onCancel: onCancelRef.current,
          onSubmit: onSubmitRef.current,
        }),
    },
  });

  useEffect(() => {
    if (!editor) return;
    if ((value || '') === lastEmittedRef.current) return;
    lastEmittedRef.current = value || '';
    editor.commands.setContent(value || '', false, {
      preserveWhitespace: 'full',
    });
  }, [editor, value]);

  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [editor, disabled]);

  useEffect(() => {
    if (disabled) closePanels();
  }, [disabled, closePanels]);

  useEffect(() => {
    if (editor) onEditorReady?.(editor);
  }, [editor, onEditorReady]);

  useEffect(() => {
    if (!editor || !autoFocus) return;
    const timeoutId = window.setTimeout(() => editor.commands.focus('end'), 0);
    return () => window.clearTimeout(timeoutId);
  }, [editor, autoFocus]);

  useEffect(() => {
    const node = rootRef.current;
    if (!node || !editor) return;
    const onSetMarkdown = (event: Event) => {
      const markdown = (event as CustomEvent<{ markdown?: string }>).detail?.markdown ?? '';
      lastEmittedRef.current = markdown;
      editor.commands.setContent(markdown || '', false, { preserveWhitespace: 'full' });
      onChange(markdown);
    };
    node.addEventListener('zunelo:set-markdown', onSetMarkdown);
    return () => node.removeEventListener('zunelo:set-markdown', onSetMarkdown);
  }, [editor, onChange]);

  const runFormat = useCallback(
    (command: FormatCommand) => {
      if (!editor || disabled) return;
      runFormatCommand(editor, command);
    },
    [editor, disabled],
  );

  const setLink = useCallback(() => {
    if (!editor || disabled) return;
    const previous = editor.getAttributes('link').href as string | undefined;
    const next = window.prompt('URL del enlace', previous || 'https://');
    if (applyComposerLinkUrl(editor, next)) {
      closePanels();
    }
  }, [editor, disabled, closePanels]);

  const insertEmoji = useCallback(
    (emoji: string) => {
      if (!editor || disabled) return;
      editor.chain().focus().insertContent(emoji).run();
      closePanels();
    },
    [editor, disabled, closePanels],
  );

  const insertMention = useCallback(
    (candidate: ComposerMentionCandidate) => {
      if (!editor || disabled) return;
      if (candidate.disabled) return;
      const href =
        candidate.personId && candidate.aliasId && candidate.platform
          ? `person-mention:${candidate.platform}:${encodeURIComponent(candidate.personId)}:${encodeURIComponent(candidate.aliasId)}`
          : `slack-mention:${candidate.id}`;
      if (!isSlackMentionHref(href) && !isPersonMentionHref(href)) return;
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'text',
          text: `@${candidate.label}`,
          marks: [{ type: 'link', attrs: { href } }],
        })
        .insertContent(' ')
        .run();
      closePanels();
    },
    [editor, disabled, closePanels],
  );

  const handleMentionCandidateUnavailable = useCallback(
    (candidate: ComposerMentionCandidate) => {
      closePanels();
      onMentionCandidateUnavailable?.(candidate);
    },
    [closePanels, onMentionCandidateUnavailable],
  );

  const mentionQ = mentionQuery.trim().toLowerCase();
  const filteredMentions = !mentionQ
    ? mentionCandidates
    : mentionCandidates.filter(
        (c) =>
          c.label.toLowerCase().includes(mentionQ) ||
          c.aliasLabel?.toLowerCase().includes(mentionQ) ||
          c.id.toLowerCase().includes(mentionQ),
      );

  const emojiSearchActive = emojiQuery.trim().length > 0;
  const filteredEmojis = useMemo(
    () => (emojiSearchActive ? filterComposerEmojis(emojiQuery) : []),
    [emojiQuery, emojiSearchActive],
  );
  const activeCategory = useMemo(
    () =>
      COMPOSER_EMOJI_CATEGORIES.find((c) => c.id === emojiCategoryId) ??
      COMPOSER_EMOJI_CATEGORIES[0],
    [emojiCategoryId],
  );
  const visibleEmojis = emojiSearchActive ? filteredEmojis : (activeCategory?.emojis ?? []);

  const togglePanel = useCallback((panel: Exclude<BottomPanel, null>) => {
    setBottomPanel((current) => (current === panel ? null : panel));
  }, []);

  const isActive = (name: string) => Boolean(editor?.isActive(name));

  const insertImage = (file: File) => {
    // Keep inline images below Firestore's document budget until Storage-backed
    // attachments are introduced; this also prevents oversized local payloads.
    if (!editor || !file.type.startsWith('image/') || file.size > 512 * 1024) return;
    const reader = new FileReader();
    reader.onload = () => {
      const src = typeof reader.result === 'string' ? reader.result : null;
      if (src) editor.chain().focus().setImage({ src, alt: file.name }).run();
    };
    reader.readAsDataURL(file);
  };

  return (
    <div
      ref={rootRef}
      className={`z-rtc ${toolbarVisible ? 'z-rtc--toolbar-open' : ''} ${className}`.trim()}
      data-testid={testId}
      style={{ ['--z-rtc-min-height' as string]: `${minHeight}px` }}
    >
      {toolbarVisible ? (
        <RichTextToolbar
          disabled={disabled}
          isActive={isActive}
          runFormat={runFormat}
          onSetLink={setLink}
          onInsertImage={enableImages ? () => imageInputRef.current?.click() : undefined}
          onSetReminder={onSetReminder}
          onRemoveReminder={onRemoveReminder}
          currentDue={currentDue}
          currentTime={currentTime}
          enableReminders={enableReminders}
          trailing={toolbarTrailing}
        />
      ) : null}

      <EditorContent editor={editor} className="z-rtc-editor" />

      {enableImages ? (
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          hidden
          aria-label="Insertar imagen"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) insertImage(file);
            event.currentTarget.value = '';
          }}
        />
      ) : null}

      <RichTextBottomChrome
        showBottomExtras={showBottomExtras}
        showToolbarToggle={showToolbarToggle}
        disabled={disabled}
        toolbarVisible={toolbarVisible}
        bottomPanel={bottomPanel}
        testId={testId}
        plusTriggerRef={plusTriggerRef}
        emojiTriggerRef={emojiTriggerRef}
        mentionTriggerRef={mentionTriggerRef}
        emojiQuery={emojiQuery}
        emojiSearchActive={emojiSearchActive}
        emojiCategoryId={emojiCategoryId}
        visibleEmojis={visibleEmojis}
        mentionQuery={mentionQuery}
        filteredMentions={filteredMentions}
        onToggleToolbar={() => setToolbarVisible((v) => !v)}
        onTogglePanel={togglePanel}
        onClosePanels={closePanels}
        onSetLink={setLink}
        onRunFormat={runFormat}
        onEmojiQuery={setEmojiQuery}
        onEmojiCategory={setEmojiCategoryId}
        onInsertEmoji={insertEmoji}
        onMentionQuery={setMentionQuery}
        onInsertMention={insertMention}
        onMentionCandidateUnavailable={
          onMentionCandidateUnavailable ? handleMentionCandidateUnavailable : undefined
        }
      />
    </div>
  );
};
